import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endpoints } from '../../api/endpoints';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { ErrorNotice, Loading } from '../../components/ui';
import { Field, TextInput } from '../../components/ui/Form';

interface Company {
  legal_name: string;
  trade_name: string | null;
  gstin: string | null;
  pan: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  phone: string | null;
  email: string | null;
  print_tagline: string | null;
}

type Form = {
  legalName: string;
  tradeName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
  pan: string;
  printTagline: string;
};

/**
 * The shop's own details, as they head a bill.
 *
 * This is the first thing an owner setting the shop up wants to fill in, and
 * every one of these fields prints: the name and tagline at the top, the
 * address and phone beneath, the GSTIN and PAN that a tax invoice must carry.
 * So it sits at the top of Settings, in plain groups a shopkeeper reads
 * straight down — who you are, how to reach you, your tax numbers — rather than
 * a wall of inputs.
 */
export function BusinessDetails({ editable }: { editable: boolean }) {
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<unknown>(null);

  const company = useQuery({
    queryKey: ['company-details'],
    queryFn: () => endpoints.organisation.company(),
  });

  useEffect(() => {
    const c = company.data?.data as unknown as Company | null;
    if (c && !form) {
      setForm({
        legalName: c.legal_name ?? '',
        tradeName: c.trade_name ?? '',
        phone: c.phone ?? '',
        email: c.email ?? '',
        address: c.address ?? '',
        city: c.city ?? '',
        state: c.state ?? '',
        pincode: c.pincode ?? '',
        gstin: c.gstin ?? '',
        pan: c.pan ?? '',
        printTagline: c.print_tagline ?? '',
      });
    }
  }, [company.data, form]);

  const save = useMutation({
    mutationFn: (body: Form) => endpoints.organisation.updateCompany(body),
    onSuccess: () => {
      toast('Business details saved', 'These print on every bill from now on.');
      void client.invalidateQueries({ queryKey: ['company-details'] });
      void client.invalidateQueries({ queryKey: ['companies'] });
      setError(null);
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (form) save.mutate(form);
  };

  if (company.isLoading || !form) return <Loading rows={3} />;

  const set = (k: keyof Form) => (v: string): void => setForm({ ...form, [k]: v });

  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Business details</h2>
        <span className="text-2xs text-ink-soft">Printed on every bill and receipt</span>
      </div>
      <ErrorNotice error={error} />

      <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="rounded-xl border border-silver-100 bg-surface p-4 shadow-card">
        <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-ink-soft">Who you are</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Shop name" hint="The big name at the top of the bill." required>
            <TextInput value={form.tradeName || form.legalName} onChange={set('tradeName')} maxLength={200} disabled={!editable} />
          </Field>
          <Field label="Legal name" hint="The registered name, if different.">
            <TextInput value={form.legalName} onChange={set('legalName')} maxLength={200} disabled={!editable} />
          </Field>
        </div>
        <Field label="Tagline" hint="A short line under the name, e.g. “Hallmarked BIS 916 gold · Since 1982”.">
          <TextInput value={form.printTagline} onChange={set('printTagline')} maxLength={200} disabled={!editable} />
        </Field>

        <p className="mb-2 mt-4 text-2xs font-medium uppercase tracking-wide text-ink-soft">How to reach you</p>
        <Field label="Address" hint="Street and area.">
          <TextInput value={form.address} onChange={set('address')} maxLength={400} disabled={!editable} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="City"><TextInput value={form.city} onChange={set('city')} maxLength={120} disabled={!editable} /></Field>
          <Field label="State"><TextInput value={form.state} onChange={set('state')} maxLength={120} disabled={!editable} /></Field>
          <Field label="Pincode"><TextInput value={form.pincode} onChange={set('pincode')} maxLength={12} disabled={!editable} /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Phone"><TextInput value={form.phone} onChange={set('phone')} maxLength={40} disabled={!editable} /></Field>
          <Field label="Email"><TextInput value={form.email} onChange={set('email')} maxLength={120} disabled={!editable} /></Field>
        </div>

        <p className="mb-2 mt-4 text-2xs font-medium uppercase tracking-wide text-ink-soft">Tax numbers</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="GSTIN" hint="Needed for a tax invoice to carry GST.">
            <TextInput value={form.gstin} onChange={(v) => set('gstin')(v.toUpperCase())} maxLength={15} disabled={!editable} />
          </Field>
          <Field label="PAN">
            <TextInput value={form.pan} onChange={(v) => set('pan')(v.toUpperCase())} maxLength={10} disabled={!editable} />
          </Field>
        </div>

        {editable ? (
          <button className="btn-primary mt-4" disabled={save.isPending || !form.tradeName.trim() && !form.legalName.trim()}>
            {save.isPending ? 'Saving…' : 'Save business details'}
          </button>
        ) : null}
      </form>
    </section>
  );
}
